import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Breadcrumbs = ({ customItems = null }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const routeMap = {
    '/dashboard-overview': { label: 'Dashboard', icon: 'LayoutDashboard' },
    '/store-management': { label: 'Store Management', icon: 'Store' },
    '/branding-cost-calculator': { label: 'Cost Calculator', icon: 'Calculator' },
    '/installation-tracking': { label: 'Installations', icon: 'Wrench' },
    '/store-layout-manager': { label: 'Store Layouts', icon: 'Layout' },
    '/brand-analytics': { label: 'Analytics', icon: 'BarChart3' }
  };

  const generateBreadcrumbs = () => {
    if (customItems) {
      return customItems;
    }

    const pathSegments = location?.pathname?.split('/')?.filter(Boolean);
    const breadcrumbs = [{ label: 'Home', path: '/dashboard-overview', icon: 'Home' }];

    let currentPath = '';
    pathSegments?.forEach((segment) => {
      currentPath += `/${segment}`;
      const routeInfo = routeMap?.[currentPath];
      
      if (routeInfo) {
        breadcrumbs?.push({
          label: routeInfo?.label,
          path: currentPath,
          icon: routeInfo?.icon
        });
      } else {
        // Handle dynamic segments or unknown routes
        const formattedLabel = segment?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
        
        breadcrumbs?.push({
          label: formattedLabel,
          path: currentPath,
          icon: 'ChevronRight'
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs?.length <= 1) {
    return null;
  }

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <nav className="flex items-center gap-2 py-3 text-sm" aria-label="Breadcrumb">
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
        {breadcrumbs?.map((item, index) => {
          const isLast = index === breadcrumbs?.length - 1;
          const isClickable = item?.path && !isLast;

          return (
            <div key={item?.path || index} className="flex items-center gap-2 flex-shrink-0">
              {index > 0 && (
                <Icon 
                  name="ChevronRight" 
                  size={14} 
                  className="text-muted-foreground flex-shrink-0" 
                />
              )}
              {isClickable ? (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleNavigation(item?.path)}
                  className="gap-1.5 text-muted-foreground hover:text-foreground transition-colors duration-200 px-2 py-1 h-auto min-w-0"
                >
                  {item?.icon && (
                    <Icon name={item?.icon} size={14} className="flex-shrink-0" />
                  )}
                  <span className="truncate max-w-32 sm:max-w-none">{item?.label}</span>
                </Button>
              ) : (
                <div className="flex items-center gap-1.5 px-2 py-1">
                  {item?.icon && (
                    <Icon 
                      name={item?.icon} 
                      size={14} 
                      className={`flex-shrink-0 ${isLast ? 'text-foreground' : 'text-muted-foreground'}`} 
                    />
                  )}
                  <span 
                    className={`truncate max-w-32 sm:max-w-none font-medium ${
                      isLast ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {item?.label}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default Breadcrumbs;